import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = getArticleBySlug(slug || '');
  const related = article ? getRelatedArticles(article.id, 3) : [];

  if (!article) {
    return (
      <div className="pt-16">
        <div className="w-full px-6 md:px-10 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">抱歉，您要查看的文章可能已被移除或链接无效</p>
          <Link to="/" className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '开云电竞',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.kaiyunesports.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 开云电竞`}
        description={article.excerpt}
        keywords={`开云电竞,${article.game},${article.tags.join(',')},电竞竞技平台`}
        canonicalUrl={`https://www.kaiyunesports.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.image}
        ogType="article"
        schemaData={articleSchema}
      />

      <div className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: '新闻资讯', path: '/category/news' },
            { name: article.title },
          ]}
        />

        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-6 md:px-10">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.game}</span>
                <span className="text-xs text-foreground-400">{article.date}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                </span>
              </div>
            </div>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-loose
                prose-strong:text-foreground-950"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 rounded-full text-xs text-foreground-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="w-full py-14 bg-background-100">
            <div className="w-full px-6 md:px-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/article/${r.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={r.image}
                        alt={r.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-accent-600 font-medium">{r.category}</span>
                        <span className="text-xs text-foreground-400">{r.date}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {r.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}